import React, { Fragment, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Divider, Typography } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { DrawerLayoutWrapper } from 'common';
import { format, parseISO } from 'date-fns';
import * as R from 'ramda';
import { DrawerContext } from 'common/drawer/DrawerContext';
import { Info } from '@mui/icons-material';
import palette from 'library/theme/palette';

import { styled } from '@mui/material/styles';

const CheckIcon = styled(CheckCircleOutlineOutlinedIcon)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontSize: 120,
}));

const ErrorIcon = styled(CancelOutlinedIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  fill: 'var(--states-error-900, #B71C1C)',
  height: '80px',
  width: '80px',
}));

const OrderConfirmation = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  padding: '10px',
  textAlign: 'center',
  width: '100%',
});

const OneToOneConfirmation = ({ reconsignmentState, reset, result }) => {
  const {
    fromLocation,
    fullFilment,
    inventoryOwner,
    origin,
    product,
    rvp,
    toLocation,
    transferDate,
    volume,
  } = reconsignmentState;

  const { setDrawerComponentProps } = useContext(DrawerContext);
  const [inventoryStatusValue, setInventoryStatusValue] = useState(null);
  const [consignmentStatusValue, setConsignmentStatusValue] = useState(null);
  const handleDrawerClose = () => {
    setDrawerComponentProps({
      Component: '',
      forceCloseSelector: () => false,
      holdOpenSelector: () => false,
      refreshNotifications: false,
      resetWizard: undefined,
      setCurrentDialogOpen: () => {},
      type: '',
    });
  };

  useEffect(() => {
    if (fullFilment && fullFilment[0].label !== null) {
      if (['Any Inventory', 'Any Uncontrolled', 'Any Controlled'].includes(fullFilment[0].label)) {
        setInventoryStatusValue('ANY');
      } else if (
        ['Any Available', 'Uncontrolled Available', 'Controlled Available'].includes(
          fullFilment[0].label
        )
      ) {
        setInventoryStatusValue('AVAILABLE');
      } else if (
        ['Any In-Transit', 'Uncontrolled In-Transit', 'Controlled In-Transit'].includes(
          fullFilment[0].label
        )
      ) {
        setInventoryStatusValue('IN-TRANSIT');
      }
    }
  }, [fullFilment, setInventoryStatusValue]);

  useEffect(() => {
    if (fullFilment && fullFilment[0].label !== null) {
      if (['Any Inventory', 'Any Available', 'Any In-Transit'].includes(fullFilment[0].label)) {
        setConsignmentStatusValue('ANY');
      } else if (
        ['Any Uncontrolled', 'Uncontrolled Available', 'Uncontrolled In-Transit'].includes(
          fullFilment[0].label
        )
      ) {
        setConsignmentStatusValue('UNCONTROLLED');
      } else if (
        ['Any Controlled', 'Controlled Available', 'Controlled In-Transit'].includes(
          fullFilment[0].label
        )
      ) {
        setConsignmentStatusValue('CONTROLLED');
      }
    }
  }, [fullFilment, setConsignmentStatusValue]);

  const isGasoline = R.equals(R.T(), R.prop('isGasoline', product));
  const summaryFieldsMap = {
    Details: [
      {
        id: 1,
        label: 'Reconsignment ID',
        value: result?.reconsignmentId,
      },
      {
        id: 2,
        label: 'Transfer Date',
        value: !R.isNil(transferDate)
          ? format(parseISO(transferDate), 'MM/dd/yyyy')
          : 'Invalid Date',
      },
      {
        id: 3,
        label: 'Affiliate',
        value: inventoryOwner?.customerNameShort,
      },
      {
        id: 4,
        label: 'Product',
        value: product?.productName,
      },
      isGasoline
        ? {
            id: 5,
            label: 'RVP',
            value: rvp,
          }
        : null,
      {
        id: 6,
        label: 'From Location',
        value: fromLocation?.locationName,
      },
      {
        id: 7,
        label: 'To Location',
        value: toLocation?.locationName,
      },
      {
        id: 8,
        label: 'Origin(s)',
        sx: { display: 'block', whiteSpace: 'pre-wrap' },
        value: origin.length > 0 ? origin.map(R.prop('originName')).join('\n') : 'Any',
      },
    ],
    TransferVolume: [
      {
        id: 9,
        label: 'Transfer Volume',
        value: `${volume} bbl`,
      },
      {
        id: 10,
        label: 'Inventory Status',
        value: inventoryStatusValue,
      },
      {
        id: 11,
        label: 'Consignment Status',
        value: consignmentStatusValue,
      },
    ],
  };

  const renderLabel = str => {
    const res = str.replace(/([A-Z])/g, ' $1');
    return res.charAt(0).toUpperCase() + res.slice(1);
  };

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={reset} sx={{ width: 150 }} variant="outlined">
        Create New
      </Button>
      <Button onClick={handleDrawerClose} sx={{ width: 150 }}>
        Done
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={actionButtons}
      headerButtonText="CLOSE"
      onHeaderButtonClick={handleDrawerClose}
      title="New 1 to 1 Reconsignment"
    >
      <OrderConfirmation>
        <Box display="flex">{result.status === 'CANNOT BOOK' ? <ErrorIcon /> : <CheckIcon />}</Box>
        {result.status === 'SCHEDULED' ? (
          <Typography
            sx={{
              '& > .MuiChip-label': { px: 0 },
              alignItems: 'center',
              background: palette.success.light,
              borderRadius: '4px',
              display: 'flex',
              fontSize: 12,
              fontWeight: 'bold',
              height: 20,
              justifyContent: 'center',
              padding: '2px 8px',
            }}
            variant="Caption"
          >
            Scheduled
          </Typography>
        ) : result.status === 'CANNOT BOOK' ? (
          <Typography
            sx={{
              '& > .MuiChip-label': { px: 0 },
              alignItems: 'center',
              background: 'var(--states-error-900, #B71C1C)',
              borderRadius: '4px',
              color: 'var(--Graphic-White, #FFFFFF)',
              display: 'flex',
              fontSize: 12,
              height: 20,
              justifyContent: 'center',
              padding: '2px 8px',
            }}
            variant="Caption"
          >
            Cannot Book
          </Typography>
        ) : (
          <Typography
            sx={{
              '& > .MuiChip-label': { px: 0 },
              alignItems: 'center',
              background: 'var(--Primary-Green-100, #B2D7C3)',
              borderRadius: '4px',
              display: 'flex',
              fontSize: '12px',
              fontWeight: 'bold',
              height: 20,
              justifyContent: 'center',
              padding: '2px 8px',
            }}
            variant="Caption"
          >
            Booked
          </Typography>
        )}
        {result.status === 'SCHEDULED' ? (
          <>
            <Typography
              sx={{
                lineHeight: '20px',
                margin: '12px 0 16px 0',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
              }}
              variant="h6"
            >
              RECONSIGNMENT{'\n'}SUBMITTED AND{'\n'}
              SCHEDULED.
            </Typography>
            <Box
              sx={{
                alignItems: { sm: 'flex-start', xs: 'center' },
                alignSelf: 'stretch',
                background: palette.success.light,
                borderRadius: '4px',
                direction: 'row',
                display: 'flex',
                gap: '12px',
                margin: '0 8px 16px 8px',
                padding: '16px',
                pl: 2,
              }}
            >
              <Info
                sx={{
                  background: palette.success.light,
                  borderRadius: '4px',
                  color: palette.graphic.main,
                }}
              />
              <Box>
                <Typography sx={{ textAlign: 'left' }} variant="body2">
                  {result.message}
                </Typography>
              </Box>
            </Box>
          </>
        ) : result.status === 'CANNOT BOOK' ? (
          <>
            <Typography
              sx={{
                lineHeight: '20px',
                margin: '12px 0 16px 0',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
              }}
              variant="h6"
            >
              RECONSIGNMENT{'\n'}SUBMITTED AND{'\n'}
              CANNOT BOOK.
            </Typography>
            <Box
              sx={{
                alignItems: { sm: 'flex-start', xs: 'center' },
                alignSelf: 'stretch',
                background: 'var(--states-success-100, #BBDEFB)',
                borderRadius: '4px',
                direction: 'row',
                display: 'flex',
                gap: '12px',
                margin: '0 8px 16px 8px',
                padding: '16px',
                pl: 2,
              }}
            >
              <Info
                sx={{
                  background: 'var(--states-success-100, #BBDEFB)',
                  borderRadius: '4px',
                  color: '#B7BCC5',
                }}
              />
              <Box>
                <Typography sx={{ textAlign: 'left' }} variant="body2">
                  {result.message}
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          <Typography
            sx={{
              lineHeight: '20px',
              margin: '12px 0 16px 0',
              textAlign: 'center',
              whiteSpace: 'pre-wrap',
            }}
            variant="h6"
          >
            RECONSIGNMENT{'\n'}SUBMITTED AND BOOKED.
          </Typography>
        )}
      </OrderConfirmation>
      <Divider gap={32} />
      <Box sx={{ mt: 3, p: '0 2', pl: 2 }}>
        <Typography variant="body1">Order Summary</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: 3,
          width: '100%',
        }}
      >
        {Object.keys(summaryFieldsMap).map((section, i) => {
          const fields = summaryFieldsMap[section];
          return (
            <Box
              key={section}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  alignContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'graphic.light',
                  display: 'flex',
                  justifyContent: 'space-between',
                  minHeight: 36,
                  pl: 3.5,
                  pr: 2,
                  width: '100%',
                }}
              >
                <Typography variant="body1">{`${i + 1}. ${renderLabel(section)}`}</Typography>
              </Box>
              {fields.filter(Boolean).map(({ id, label, value }) => (
                <Fragment key={id}>
                  <Box
                    sx={{
                      alignContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'space-between',
                      minHeight: 48,
                      pl: 3.5,
                      pr: 2,
                      py: 1,
                      width: '100%',
                    }}
                  >
                    <Typography variant="body1">{label}</Typography>
                    <Typography
                      sx={{
                        color: 'text.primary',
                        ml: 1,
                        textAlign: 'right',
                        whiteSpace: 'pre-wrap',
                      }}
                      variant="body1"
                    >
                      {value}
                    </Typography>
                  </Box>
                </Fragment>
              ))}
            </Box>
          );
        })}
      </Box>
    </DrawerLayoutWrapper>
  );
};

OneToOneConfirmation.propTypes = {
  reconsignmentState: PropTypes.shape({
    fromLocation: PropTypes.shape({
      locationName: PropTypes.string,
    }),
    fullFilment: PropTypes.shape({}),
    inventoryOwner: PropTypes.shape({
      customerNameShort: PropTypes.string,
    }),
    origin: PropTypes.arrayOf(),
    product: PropTypes.shape({
      productName: PropTypes.string,
    }),
    rvp: PropTypes.string,
    toLocation: PropTypes.shape({
      locationName: PropTypes.string,
    }),
    transferDate: PropTypes.string,
    volume: PropTypes.string,
  }).isRequired,
  reset: PropTypes.func.isRequired,
  result: PropTypes.shape({
    message: PropTypes.string,
    reconsignmentId: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

export default OneToOneConfirmation;
